<template>
    <div class="filhantering-filer">
        <ModalContainerMediaPreview
            v-if="showModal"
            @close="onClose"
            :show-modal="showModal"
            :file-meta="{
                patient: patient.id,
                id: fileId,
            }"
            :files="decoratedFiles"
            :current-index="currentFileIndex"
            :tabId="tabId"
            :enableInlineContainer="false"
            :isGenerell="!patientBoundFiles"
        />
        <template v-if="patientBoundFiles">
            <template v-if="patientSelected">
                <h2>Filer för {{ personId_formatted }} {{ patientName }}</h2>
            </template>
            <template v-else>
                <h2>Du måste sätta en aktiv patient för att se en fillistning.</h2>
            </template>
        </template>
        <template v-else>
            <h2>Allmän filkö</h2>
            <div class="allman-filko-info">
                <img src="@/assets/info_dark.svg" />
                <span>Bilder och filer som ännu inte kopplats till en patient</span>
            </div>
        </template>
        <KlinikenTable
            :columns="visibleColumns"
            :items="decoratedFiles"
            :highlightedItems="highlightedFiles"
            :dropdown-actions="actions"
            @show="showFileModal"
            @edit="openFileTab"
            @connectToPatient="(data) => openFileTab({ ...data, isGenerell: true })"
            @deleteFile="(data) => openFileTab({ ...data, isGenerell: true, deleteFile: true })"
        />
    </div>
</template>

<script setup>
    import { toRefs, defineProps, computed } from "vue";
    import { useStore } from "@/store";

    const store = useStore();

    const props = defineProps({
        patientBoundFiles: {
            required: false,
            type: Boolean,
            default: true,
        },
    });

    const { patientBoundFiles } = toRefs(props);

    const personId = computed(() => store.getters["patientData/personId"]);
    const personId_formatted = computed(() => store.getters["patientData/personId_formatted"]);
    const patientName = computed(() => store.getters["patientData/namn_reverse"]);

    const visibleColumns = computed(() => {
        return columns.filter((col) => !col.hide);
    });

    const columns = [
        {
            name: "dokumenttypNamn",
            title: "Typ av fil",
        },
        {
            name: "decoratedAuthorDate",
            title: "Inläsningsdatum",
        },
        {
            name: "decoratedDocumentDate",
            title: "Dokumentets datum",
        },
        {
            name: "filformat",
            title: "Filformat",
        },
        {
            name: "decoratedSize",
            title: "Storlek",
        },
        {
            name: "ursprungligt_filnamn",
            title: "Filnamn",
            trimOverflow: true,
        },
        {
            name: "decoratedComment",
            title: "Kommentar",
        },
        {
            name: "personId_formatted",
            title: "PersonID",
            hide: patientBoundFiles?.value,
            finished: (item) => {
                // Visar en bock om personId finns
                return item.personId_formatted;
            },
        },
    ];

    const actions = [
        {
            name: "connectToPatient",
            title: "Koppla fil till patient",
            show: (item) => {
                return !patientBoundFiles?.value && !item.personId;
            },
        },
        {
            name: "show",
            title: "Visa fil",
            show: (item) => {
                return (patientBoundFiles?.value && item.patient) || !item.personId;
            },
        },
        {
            name: "edit",
            title: "Redigera fildata",
            show: (item) => {
                return patientBoundFiles?.value && item.patient;
            },
        },
        {
            name: "deleteFile",
            title: "Radera fil",
            show: (item) => {
                return !patientBoundFiles?.value && !item.personId;
            },
        },
    ];
</script>

<script>
    import BaseTab from "@/tabs/mixins/BaseTab";
    import fileIcon from "@/assets/filer.svg";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import { mapGetters } from "vuex";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import { bytesToSize } from "@/tabs/Filhantering/Utils.js";
    import { openDialog } from "@/utils";
    import FilhanteringRedigeraFil from "@/tabs/Filhantering/FilhanteringRedigeraFil.vue";
    import ModalContainerMediaPreview from "@/components/generic/preview/media/ModalContainerMediaPreview.vue";

    export default {
        name: "FilhanteringFiler",

        components: {
            KlinikenTable,
            ModalContainerMediaPreview,
        },
        extends: BaseTab,

        data() {
            return {
                files: [],
                fileMeta: null,
                highlightedFiles: [],
                dokumenttyper: [],
                showModal: false,
                fileId: null,
                currentFileIndex: 0,
            };
        },
        computed: {
            decoratedFiles() {
                const files = [...this.files].map((file) => ({
                    ...file,
                    decoratedDocumentDate: file.documentTime?.substring(0, 10) ?? "",
                    decoratedAuthorDate: file.authorTime?.substring(0, 10) ?? file.created_at?.substring(0, 10),
                    decoratedSize: bytesToSize(file.filstorlek),
                    decoratedComment: file.documentTitle ? file.documentTitle.substring(0, 240) : "",
                    dokumenttypNamn: this.getDokumenttypNamn(file.dokument_typ) ?? file.dokument_typ_namn,
                }));

                // Sort items by most recent first
                files.sort((a, b) => {
                    const aDate = a.authorTime ?? a.created_at;
                    const bDate = b.authorTime ?? b.created_at;

                    if (aDate < bDate) {
                        return 1;
                    } else if (aDate > bDate) {
                        return -1;
                    } else {
                        return 0;
                    }
                });

                return files;
            },
            ...mapGetters("patientData", ["patient"]),
            ...mapGetters("tabs", ["tabs"]),

            patientSelected() {
                return Object.keys(this.patient).length !== 0;
            },
        },

        watch: {
            patient: function () {
                if (this.patientBoundFiles) {
                    this.updateFilesFromBackend();
                }
            },
        },
        async created() {
            try {
                const response = await klinikenApi.get("filhantering/dokumenttyp/", getConfig());
                this.dokumenttyper = response.data.results;
            } catch (e) {
                this.dokumenttyper = [];
            }

            this.updateFilesFromBackend();

            this.$on("edit", async (data) => {
                await this.openFileTab(data);
            });

            this.$on("show", async (data) => {
                await this.showFileModal(data);
            });

            this.$root.$on("file-changed", () => {
                // Om vi har Allmän filkö öppen samtidigt som Filer (patientbundna) ska inte ändring i patientbunden fil
                // trigga uppdatering av Allmän filkö, för att inte störa pågående session.
                if (this.patientBoundFiles) {
                    // TODO Here we could possibly start an animation that informs us that the data is changing
                    // Or we could do the harder thing and figure out how to keep data in sync on the client without
                    // going back to the source of truth (the backend)
                    this.updateFilesFromBackend();
                }
            });

            this.$root.$on("file-connected", (generellFil) => {
                if (!this.patientBoundFiles) {
                    const fileIndex = this.files.findIndex((file) => file.id === generellFil.id);
                    if (fileIndex !== -1) {
                        // Använder Vue.set för att säkerställa reaktivitet mot decoratedFiles
                        this.$set(this.files, fileIndex, {
                            ...this.files[fileIndex],
                            personId: generellFil.personId,
                            personId_formatted: generellFil.personId_formatted,
                            documentTime: generellFil.documentTime.toISOString(true),
                            documentTitle: generellFil.documentTitle,
                            dokument_typ: generellFil.dokument_typ,
                        });

                        // Markerar raden i tabellen
                        this.highlightedFiles.push(generellFil.id);
                    }
                } else {
                    this.updateFilesFromBackend();
                }
            });

            this.$root.$on("file-deleted", (generellFilId) => {
                if (!this.patientBoundFiles) {
                    const fileIndex = this.files.findIndex((file) => file.id === generellFilId);
                    if (fileIndex !== -1) {
                        this.files.splice(fileIndex, 1);
                    }
                }
            });
        },

        beforeDestroy() {
            this.$root.$off("file-changed");
            this.$root.$off("file-connected");
            this.$root.$off("file-deleted");
        },

        methods: {
            async openFileTab(data) {
                await this.$store.dispatch("tabs/closeTab", FilhanteringRedigeraFil);

                await this.$store.dispatch("tabs/openTab", {
                    component: FilhanteringRedigeraFil,
                    parameters: data,
                    customPatientBound: false,
                    tabName: "Koppla fil till patient",
                });
            },

            async updateFilesFromBackend() {
                const config = getConfig({}, "Filer");
                try {
                    if (this.patientBoundFiles) {
                        if (this.patientSelected) {
                            const response = await klinikenApi.get(`filhantering/${this.patient.id}/fil/list/`, config);
                            this.files = response.data;
                        }
                    } else {
                        const response = await klinikenApi.get("filhantering/generellafiler/", config);
                        this.files = response.data.results;
                    }
                } catch (error) {
                    openDialog(getErrorMessage(error), "Kunde inte ladda filer");
                }
            },

            getDokumenttypNamn(id) {
                const dokumenttyp = this.dokumenttyper.find((dt) => dt.id === id);
                return dokumenttyp?.namn;
            },
            async showFileModal(data) {
                this.fileId = data.id;
                this.currentFileIndex = this.decoratedFiles.findIndex((f) => f.id === data.id);
                this.showModal = true;
            },
            onClose() {
                this.showModal = false;
            },
        },
        icon: fileIcon,
        patientBound: true,
        tabName: "Filer",
    };
</script>

<style lang="scss" scoped>
    .filhantering-filer {
        .allman-filko-info {
            display: flex;
            gap: 0.5rem;
            margin: 1rem 0;
        }
    }
</style>
